import React from 'react';
import type { FormatCommand } from './richTextFormat';
import { ReminderPopover } from './ReminderPopover';

function ToolbarDivider() {
  return <span className="z-rtc-divider" aria-hidden="true" />;
}

function FormatIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

export function CompactTextFormatIcon() {
  return (
    <FormatIcon>
      <path
        d="M3.5 5h8M7.5 5v10M12 9h4.5M14.25 9v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </FormatIcon>
  );
}

export function CompactImageIcon() {
  return (
    <FormatIcon>
      <rect x="3" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="7" cy="8" r="1.2" fill="currentColor" />
      <path
        d="m5 14 3.5-3 2.4 2 2-2 2.1 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </FormatIcon>
  );
}

export function CompactReminderIcon() {
  return (
    <FormatIcon>
      <path
        d="M10 3.5a3 3 0 0 0-3 3v2.2c0 .6-.2 1.2-.6 1.7L5.3 12a.8.8 0 0 0 .6 1.3h8.2a.8.8 0 0 0 .6-1.3l-1.1-1.6a2.6 2.6 0 0 1-.6-1.7V6.5a3 3 0 0 0-3-3ZM8.5 14.5a1.5 1.5 0 0 0 3 0"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </FormatIcon>
  );
}

interface FormatButtonProps {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function FormatButton({ label, active, disabled, onClick, children }: FormatButtonProps) {
  return (
    <button
      type="button"
      className={`z-rtc-btn ${active ? 'is-active' : ''}`}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export interface RichTextToolbarProps {
  disabled: boolean;
  isActive: (name: string) => boolean;
  runFormat: (command: FormatCommand) => void;
  onSetLink: () => void;
  onInsertImage?: () => void;
  onSetReminder?: (due: string, time?: string) => void;
  onRemoveReminder?: () => void;
  currentDue?: string;
  currentTime?: string;
  enableReminders?: boolean;
  trailing?: React.ReactNode;
}

export const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  disabled,
  isActive,
  runFormat,
  onSetLink,
  onInsertImage,
  onSetReminder,
  onRemoveReminder,
  currentDue,
  currentTime,
  enableReminders,
  trailing,
}) => {
  const [isReminderOpen, setIsReminderOpen] = React.useState(false);
  const reminderTriggerRef = React.useRef<HTMLButtonElement | null>(null);

  const showReminder = enableReminders || Boolean(onSetReminder);

  return (
    <div className="z-rtc-toolbar" role="toolbar" aria-label="Formato">
      <FormatButton
        label="Negrita"
        active={isActive('bold')}
        disabled={disabled}
        onClick={() => runFormat('bold')}
      >
        <span className="z-rtc-glyph z-rtc-glyph--bold">B</span>
      </FormatButton>
      <FormatButton
        label="Cursiva"
        active={isActive('italic')}
        disabled={disabled}
        onClick={() => runFormat('italic')}
      >
        <span className="z-rtc-glyph z-rtc-glyph--italic">I</span>
      </FormatButton>
      <FormatButton
        label="Tachado"
        active={isActive('strike')}
        disabled={disabled}
        onClick={() => runFormat('strike')}
      >
        <span className="z-rtc-glyph z-rtc-glyph--strike">S</span>
      </FormatButton>

      <ToolbarDivider />

      <FormatButton
        label="Enlace"
        active={isActive('link')}
        disabled={disabled}
        onClick={onSetLink}
      >
        <FormatIcon>
          <path
            d="M8.5 11.5 11.5 8.5M7.2 9.2l-1.4 1.4a3 3 0 1 0 4.2 4.2l1.4-1.4M12.8 10.8l1.4-1.4a3 3 0 1 0-4.2-4.2L8.6 6.6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </FormatIcon>
      </FormatButton>

      <ToolbarDivider />

      <FormatButton
        label="Lista numerada"
        active={isActive('orderedList')}
        disabled={disabled}
        onClick={() => runFormat('orderedList')}
      >
        <FormatIcon>
          <path
            d="M4 5.5h1.2M4 10h1.5M4 14.5h1.2M8 5.5h8M8 10h8M8 14.5h8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </FormatIcon>
      </FormatButton>
      <FormatButton
        label="Lista con viñetas"
        active={isActive('bulletList')}
        disabled={disabled}
        onClick={() => runFormat('bulletList')}
      >
        <FormatIcon>
          <circle cx="4.5" cy="5.5" r="1" fill="currentColor" />
          <circle cx="4.5" cy="10" r="1" fill="currentColor" />
          <circle cx="4.5" cy="14.5" r="1" fill="currentColor" />
          <path
            d="M8 5.5h8M8 10h8M8 14.5h8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </FormatIcon>
      </FormatButton>

      <ToolbarDivider />

      <FormatButton
        label="Cita"
        active={isActive('blockquote')}
        disabled={disabled}
        onClick={() => runFormat('blockquote')}
      >
        <FormatIcon>
          <path
            d="M5 5v10M8 7.5h7M8 10.5h5.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </FormatIcon>
      </FormatButton>

      <ToolbarDivider />

      <FormatButton
        label="Código en línea"
        active={isActive('code')}
        disabled={disabled}
        onClick={() => runFormat('code')}
      >
        <FormatIcon>
          <path
            d="M7.5 6.5 4 10l3.5 3.5M12.5 6.5 16 10l-3.5 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </FormatIcon>
      </FormatButton>
      {onInsertImage ? (
        <FormatButton
          label="Insertar imagen"
          active={false}
          disabled={disabled}
          onClick={onInsertImage}
        >
          <CompactImageIcon />
        </FormatButton>
      ) : null}
      <FormatButton
        label="Bloque de código"
        active={isActive('codeBlock')}
        disabled={disabled}
        onClick={() => runFormat('codeBlock')}
      >
        <FormatIcon>
          <rect
            x="3.5"
            y="4.5"
            width="13"
            height="11"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <path
            d="M7.5 8 5.8 10 7.5 12M12.5 8 14.2 10 12.5 12"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </FormatIcon>
      </FormatButton>
      {showReminder ? (
        <>
          <button
            ref={reminderTriggerRef}
            type="button"
            className={`z-rtc-btn ${isReminderOpen || currentDue ? 'is-active' : ''}`}
            title="Recordatorio"
            aria-label="Recordatorio"
            aria-haspopup="dialog"
            disabled={disabled}
            onClick={() => setIsReminderOpen((v) => !v)}
            data-testid="toolbar-reminder-btn"
          >
            <CompactReminderIcon />
          </button>
          <ReminderPopover
            open={isReminderOpen}
            anchorRef={reminderTriggerRef}
            onClose={() => setIsReminderOpen(false)}
            onSelectReminder={(due: string, time?: string) => {
              onSetReminder?.(due, time);
              setIsReminderOpen(false);
            }}
            onRemoveReminder={onRemoveReminder}
            currentDue={currentDue}
            currentTime={currentTime}
          />
        </>
      ) : null}
      {trailing ? <div className="z-rtc-toolbar-trailing">{trailing}</div> : null}
    </div>
  );
};
